/**
 * Centralized GraphQL operations. Strings only — no codegen step in this scaffold.
 * In production, consider GraphQL Code Generator with TypedDocumentNode.
 */

export const Q_ME = `
  query Me {
    me {
      user { id email name avatarUrl }
      workspaces { id name slug plan role }
      currentWorkspaceId
      creditsRemaining
    }
  }
`;

export const Q_PROJECTS = `
  query Projects {
    projects {
      id name timezone language
      connections { id platform accountLabel accountType status capabilities avatarUrl followerCount }
    }
  }
`;

export const M_CREATE_PROJECT = `
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      id name timezone language
    }
  }
`;

export const Q_DRAFTS = `
  query Drafts($limit: Int) {
    drafts(limit: $limit) {
      data {
        id projectId connectionId platform postType caption
        status scheduledAt charCount charLimit aiGenerated updatedAt meta
        lastError { code category message httpStatus retryable isAuth occurredAt }
      }
      nextCursor
    }
  }
`;

export const Q_CONVERSATIONS = `
  query Conversations($unreadOnly: Boolean) {
    conversations(unreadOnly: $unreadOnly) {
      data {
        id platform kind participantHandle lastMessageAt unread status
      }
      nextCursor
    }
  }
`;

export const Q_ANALYTICS_SUMMARY = `
  query AnalyticsSummary($range: String) {
    analyticsSummary(range: $range) {
      metrics { label value delta up }
    }
  }
`;

export const Q_ANALYTICS_HEATMAP = `
  query AnalyticsHeatmap {
    analyticsHeatmap { cells }
  }
`;

export const Q_ANALYTICS_TOP_POSTS = `
  query AnalyticsTopPosts($limit: Int) {
    analyticsTopPosts(limit: $limit) {
      id title platform impressions engagements rate when
    }
  }
`;

export const Q_CONVERSATION = `
  query Conversation($id: ID!) {
    conversation(id: $id) {
      id platform kind participantHandle unread status
      messages { id direction authorHandle body createdAt }
    }
  }
`;

export const Q_BILLING = `
  query Billing {
    billingUsage {
      plan renewsAt creditsRemaining aiBillingMode
      plans { id name price credits seats storage features aiBillingMode }
    }
  }
`;

export const Q_AUTOMATION_RULES = `
  query AutomationRules($projectId: ID) {
    automationRules(projectId: $projectId) {
      id projectId platform name cadence topics approvalMode status
      lastRunAt nextRunAt runCount successCount failureCount createdAt
    }
  }
`;

export const Q_BRAND_PROFILE = `
  query BrandProfile($projectId: ID!) {
    brandProfile(projectId: $projectId) {
      projectId voiceDescription toneOfVoice targetAudience
      contentPillars styleGuidelines tabooTopics forbiddenWords
    }
  }
`;

export const Q_CONTENT = `
  query Content($status: String, $platform: String, $limit: Int) {
    content(status: $status, platform: $platform, limit: $limit) {
      data {
        id platform postType caption status scheduledAt publishedAt
        charCount charLimit aiGenerated updatedAt externalUrl
      }
    }
  }
`;

export const Q_MEDIA = `
  query MediaAssets($limit: Int) {
    mediaAssets(limit: $limit) {
      id kind storageKey mimeType sizeBytes width height altText tags createdAt
    }
  }
`;

export const Q_TEMPLATES = `
  query CaptionTemplates($projectId: ID) {
    captionTemplates(projectId: $projectId) {
      id projectId name category platform body variables useCount createdAt
    }
  }
`;

export const Q_SOURCES = `
  query ContentSources($projectId: ID) {
    contentSources(projectId: $projectId) {
      id projectId kind label url keyword status itemCount lastFetchedAt createdAt
    }
  }
`;

export const Q_INTEGRATIONS = `
  query Integrations {
    integrations { id provider label config events status createdAt }
  }
`;

export const Q_SHELL_COUNTS = `
  query ShellCounts {
    shellCounts { unreadInbox pendingDrafts scheduledPosts }
  }
`;

export const Q_NOTIFICATIONS = `
  query Notifications($limit: Int) {
    notifications(limit: $limit) { id kind title body link read createdAt }
    unreadNotificationCount
  }
`;

export const M_MARK_NOTIF_READ = `
  mutation MarkRead($id: ID!) { markNotificationRead(id: $id) }
`;

export const M_MARK_ALL_NOTIF_READ = `
  mutation MarkAllRead { markAllNotificationsRead }
`;

export const Q_CONTENT_ITEMS = `
  query ContentItems($sourceId: ID, $search: String, $range: String, $triageStatus: String, $orderBy: String, $limit: Int) {
    contentItems(sourceId: $sourceId, search: $search, range: $range, triageStatus: $triageStatus, orderBy: $orderBy, limit: $limit) {
      id sourceId sourceLabel sourceKind title url summary used postCount relevance triageStatus publishedAt createdAt
    }
  }
`;

export const M_SET_CONTENT_TRIAGE = `
  mutation SetContentTriage($id: ID!, $status: String!) {
    setContentItemTriage(id: $id, status: $status) { id triageStatus }
  }
`;

export const M_SET_CONTENT_TRIAGE_BULK = `
  mutation SetContentTriageBulk($ids: [ID!]!, $status: String!) {
    setContentItemsTriage(ids: $ids, status: $status)
  }
`;

export const M_CREATE_CONTENT_ITEM = `
  mutation CreateContentItem($input: CreateContentItemInput!) {
    createContentItem(input: $input) {
      id sourceId sourceLabel sourceKind title url summary used postCount relevance triageStatus publishedAt createdAt
    }
  }
`;

export const Q_URL_METADATA = `
  query UrlMetadata($url: String!) {
    urlMetadata(url: $url) { url title summary image }
  }
`;

// ─── Mutations ─────────────────────────────────────────────
export const M_ADD_MEDIA = `
  mutation AddMedia($input: AddMediaInput!) {
    addMediaAsset(input: $input) { id storageKey altText tags }
  }
`;

export const M_DELETE_MEDIA = `
  mutation DeleteMedia($id: ID!) { deleteMediaAsset(id: $id) }
`;

export const M_CREATE_MEDIA_UPLOAD = `
  mutation CreateMediaUpload($input: CreateMediaUploadInput!) {
    createMediaUpload(input: $input) { uploadUrl storageKey publicUrl kind contentType expiresIn }
  }
`;

export const M_COMPLETE_MEDIA_UPLOAD = `
  mutation CompleteMediaUpload($input: CompleteMediaUploadInput!) {
    completeMediaUpload(input: $input) { id storageKey kind mimeType sizeBytes altText tags }
  }
`;

export const M_CREATE_TEMPLATE = `
  mutation CreateTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input) { id name category platform body variables }
  }
`;

export const M_DELETE_TEMPLATE = `
  mutation DeleteTemplate($id: ID!) { deleteTemplate(id: $id) }
`;

export const M_CREATE_SOURCE = `
  mutation CreateSource($input: CreateSourceInput!) {
    createContentSource(input: $input) { id kind label url keyword status }
  }
`;

export const M_TOGGLE_SOURCE = `
  mutation ToggleSource($id: ID!) { toggleContentSource(id: $id) { id status } }
`;

export const M_DELETE_SOURCE = `
  mutation DeleteSource($id: ID!) { deleteContentSource(id: $id) }
`;

export const M_REFRESH_SOURCE = `
  mutation RefreshSource($id: ID!) { refreshSource(id: $id) }
`;

export const M_UPSERT_INTEGRATION = `
  mutation UpsertIntegration($input: UpsertIntegrationInput!) {
    upsertIntegration(input: $input) { id provider label config events status }
  }
`;

export const M_DELETE_INTEGRATION = `
  mutation DeleteIntegration($id: ID!) { deleteIntegration(id: $id) }
`;

export const M_CREATE_DRAFT = `
  mutation CreateDraft($input: CreateDraftInput!) {
    createDraft(input: $input) {
      id platform postType caption status charCount charLimit
    }
  }
`;

export const M_APPROVE_DRAFT = `
  mutation ApproveDraft($id: ID!) {
    approveDraft(input: { id: $id }) { id status }
  }
`;

export const M_DELETE_DRAFT = `
  mutation DeleteDraft($id: ID!) { deleteDraft(id: $id) }
`;

export const M_SCHEDULE_DRAFT = `
  mutation ScheduleDraft($id: ID!, $when: DateTime!) {
    scheduleDraft(input: { id: $id, when: $when }) { id status scheduledAt }
  }
`;

export const M_REQUEST_CHANGES = `
  mutation RequestChanges($id: ID!, $comment: String!) {
    requestChanges(input: { id: $id, comment: $comment }) { id status }
  }
`;

export const M_REPLY_CONVERSATION = `
  mutation Reply($conversationId: ID!, $body: String!) {
    replyToConversation(input: { conversationId: $conversationId, body: $body }) {
      id body createdAt
    }
  }
`;

export const M_CREATE_AUTOMATION = `
  mutation CreateAutomation($input: CreateAutomationRuleInput!) {
    createAutomationRule(input: $input) {
      id name platform cadence approvalMode status
    }
  }
`;

export const M_PAUSE_AUTOMATION = `
  mutation PauseAutomation($id: ID!) {
    pauseAutomationRule(id: $id) { id status }
  }
`;

export const M_RUN_AUTOMATION_NOW = `
  mutation RunAutomationNow($id: ID!) {
    runAutomationRuleNow(id: $id) {
      id ruleId startedAt status generatedPostId
    }
  }
`;

export const M_UPDATE_BRAND = `
  mutation UpdateBrand($input: UpdateBrandProfileInput!) {
    updateBrandProfile(input: $input) {
      projectId voiceDescription toneOfVoice contentPillars styleGuidelines tabooTopics forbiddenWords
    }
  }
`;

export const M_TOPUP = `
  mutation TopUp($amount: Int!) {
    topUpCredits(amount: $amount)
  }
`;

export const M_CREATE_CHECKOUT = `
  mutation CreateCheckout($input: CreateCheckoutInput!) {
    createCheckout(input: $input) {
      provider sessionId redirectUrl razorpayOrderId razorpaySubscriptionId keyId
    }
  }
`;
